import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useUser, UserButton } from '@clerk/clerk-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { captureEvent } from '../lib/posthog';
import {
  ArrowLeft,
  Plus,
  CheckCircle2,
  Lightbulb,
  Video,
  Timer,
  Play,
  LayoutDashboard,
  Target,
  Brain,
  Loader2,
  Trash2,
  Download
} from 'lucide-react';
import Logo from '../components/Logo';
import UploadVideo from '../components/UploadVideo';
import VideoPlayer from '../components/VideoPlayer';
import AnalysisResult from '../components/AnalysisResult';
import LoadingView from '../components/LoadingView';
import ErrorDisplay from '../components/ErrorDisplay';
import RecordingQuestionCard from '../components/RecordingQuestionCard';
import QuestionSuccessModal from '../components/QuestionSuccessModal';
import QuestionRetryCard from '../components/QuestionRetryCard';
import DeleteVideoModal from '../components/DeleteVideoModal';
import './AnalysisPage.css';
import { getPromptById } from '../config/recordingPrompts';

// Helper function to get practice metric labels with translations
const getPracticeMetricLabels = (t) => ({
  presence: t('parameters.metrics.presence'),
  voice_expression: t('parameters.metrics.voice'),
  clarity: t('parameters.metrics.clarity'),
  authenticity: t('parameters.metrics.authenticity'),
  impact: t('parameters.metrics.impact'),
  confidence: t('parameters.metrics.confidence'),
  overall: t('parameters.metrics.overall')
});

const formatMetricLabel = (metric, t) => {
  if (!metric) return null;
  const labels = getPracticeMetricLabels(t);
  return labels[metric] || metric.replace(/_/g, ' ');
};

const renderPromptMeta = (label, value) => {
  if (!value) return null;
  return (
    <div className="analysisPage__promptMeta">
      <strong>{label}</strong>
      <span>{value}</span>
    </div>
  );
};

export default function AnalysisPage({
  file,
  onSelect,
  onRemove,
  onAnalyze,
  isLoading,
  loadingPhase = 'uploading', // 'uploading' | 'analyzing'
  result,
  errorInfo,
  onRetryAnalysis,
  viewingAnalysis,
  onBackToDashboard,
  currentAnalysisId,
  userContext,
  activeJourneyId,
  practiceCompletionNotices = [],
  hasCompletedAnalysis = false,
  onDismissPracticeNotices,
  refreshTrigger,
  uploadProgress = 0 // Real upload progress (0-100)
}) {
  const { user } = useUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Capture mission from navigation state (e.g. from Dashboard or Practice page)
  const practiceMissionFromState = useMemo(() => {
    if (location.state?.practiceMode) {
      return {
        id: location.state.missionId,
        title: location.state.missionTitle,
        description: location.state.missionDescription,
        source: 'state'
      };
    }
    return null;
  }, [location.state]);

  const [selectedPrompt, setSelectedPrompt] = useState(practiceMissionFromState);
  const [promptCleared, setPromptCleared] = useState(false);

  // Auto-select starter mission for new users (no completed analyses)
  // Only if user hasn't explicitly cleared the prompt
  useEffect(() => {
    if (!hasCompletedAnalysis && !viewingAnalysis && !id && !selectedPrompt && !practiceMissionFromState && !promptCleared) {
      const goal = userContext?.primaryGoal || 'general';
      const starterId = `baseline_${goal}`;

      setSelectedPrompt({
        id: starterId,
        title: t(`onboarding.starterScenarios.${goal}.title`),
        description: t(`onboarding.starterScenarios.${goal}.description`),
        source: 'state'
      });
    }
  }, [hasCompletedAnalysis, viewingAnalysis, id, selectedPrompt, practiceMissionFromState, userContext, t, promptCleared]);

  // Update selectedPrompt if state changes
  useEffect(() => {
    if (practiceMissionFromState) {
      setSelectedPrompt(practiceMissionFromState);
      setPromptCleared(false); // Reset cleared flag when new mission comes from state
    }
  }, [practiceMissionFromState]);

  // Reset promptCleared when file is selected
  useEffect(() => {
    if (file) {
      setPromptCleared(false);
    }
  }, [file]);

  const apiBase = import.meta.env.DEV
    ? '' // use Vite proxy in dev
    : (import.meta.env.VITE_API_URL || '');

  const [videoUrl, setVideoUrl] = useState(null);
  const [loadedAnalysis, setLoadedAnalysis] = useState(null);
  const [showDeleteVideoModal, setShowDeleteVideoModal] = useState(false);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  const [videoDeleted, setVideoDeleted] = useState(false);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const currentAnalysisData = viewingAnalysis || loadedAnalysis;
  const isViewingExisting = Boolean(currentAnalysisData);
  const showNewAnalysisFlow = !isViewingExisting && !isLoading && !result;

  // Track analysis viewed event
  const analysisViewedRef = useRef(false);
  useEffect(() => {
    if ((id || currentAnalysisId || viewingAnalysis) && !analysisViewedRef.current) {
      const analysisId = id || currentAnalysisId || viewingAnalysis?.id;
      if (analysisId) {
        // Calculate time since generation if we have created_at
        const createdAt = viewingAnalysis?.created_at || loadedAnalysis?.created_at;
        const timeSinceGeneration = createdAt
          ? Math.round((Date.now() - new Date(createdAt).getTime()) / (1000 * 60))
          : null;

        captureEvent('analysis_viewed', {
          analysis_id: analysisId,
          journey_id: activeJourneyId,
          time_since_generation_minutes: timeSinceGeneration,
        });
        analysisViewedRef.current = true;
      }
    }
  }, [id, currentAnalysisId, viewingAnalysis, loadedAnalysis, activeJourneyId]);

  const currentPromptDefinition = currentAnalysisData
    ? currentAnalysisData.recording_prompt_title
      ? { title: currentAnalysisData.recording_prompt_title, description: currentAnalysisData.recording_prompt_description }
      : (currentAnalysisData.recording_prompt_id ? getPromptById(currentAnalysisData.recording_prompt_id) : null)
    : null;

  const activeGoal = currentAnalysisData?.user_context?.primaryGoal || userContext?.primaryGoal || 'general';

  // --- Recording Question State ---
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [isAnalysisModeActive, setIsAnalysisModeActive] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastQuestionScore, setLastQuestionScore] = useState(null);
  const [questionEvaluation, setQuestionEvaluation] = useState(null);
  const [alternatives, setAlternatives] = useState([]);

  // Ref to prevent duplicate API calls
  const questionFetchRef = useRef(false);
  const questionGenerateRef = useRef(false);

  // Fetch active question on mount (scoped to active journey/dashboard)
  useEffect(() => {
    if (!user?.id || isViewingExisting) return;

    // Reset refs when journey changes
    questionFetchRef.current = false;
    questionGenerateRef.current = false;

    const fetchQuestion = async () => {
      // Prevent duplicate calls
      if (questionFetchRef.current) return;
      questionFetchRef.current = true;

      setQuestionLoading(true);
      try {
        const query = activeJourneyId ? `?journeyId=${encodeURIComponent(activeJourneyId)}` : '';
        const res = await fetch(`${apiBase}/api/recording-question/active${query}`, {
          headers: { 'X-Clerk-User-Id': user.id }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.hasActive && data.question) {
            setActiveQuestion(data.question);
            questionFetchRef.current = false; // Reset after successful fetch
          } else {
            // Don't auto-generate - only load existing questions from DB
            // Questions are generated only when:
            // 1. User clicks the refresh button (handleRefreshQuestion)
            // 2. User completes a question with score > 6.0 (handled by /complete endpoint)
            setActiveQuestion(null);
            questionFetchRef.current = false; // Reset after fetch
          }
        }
      } catch (err) {
        console.error('Failed to fetch active question:', err);
        questionFetchRef.current = false; // Reset on error
      } finally {
        setQuestionLoading(false);
      }
    };

    fetchQuestion();
  }, [user?.id, apiBase, isViewingExisting, activeJourneyId]);

  const generateNewQuestion = async (params = {}) => {
    // Prevent duplicate generation calls
    if (questionGenerateRef.current) {
      console.log('Question generation already in progress, skipping...');
      return;
    }
    questionGenerateRef.current = true;

    setQuestionLoading(true);
    try {
      const requestBody = {
        primaryGoal: userContext?.primaryGoal,
        goalSpecificContext: userContext?.goalSpecificContext,
        confidenceLevel: userContext?.confidenceLevel,
        language: i18n.language === 'he' ? 'he' : 'en',
        journeyId: activeJourneyId || null,
        ...params
      };

      const res = await fetch(`${apiBase}/api/recording-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-Id': user.id
        },
        body: JSON.stringify(requestBody)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.question) {
          setActiveQuestion(data.question);
        }
      }
    } catch (err) {
      console.error('Failed to generate question:', err);
    } finally {
      setQuestionLoading(false);
      questionGenerateRef.current = false;
    }
  };

  const handleRefreshQuestion = async () => {
    if (!activeQuestion) return;
    if (questionLoading) return; // Prevent multiple simultaneous calls

    setQuestionLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/recording-question/${activeQuestion.id}/replace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-Id': user.id
        },
        body: JSON.stringify({})
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('Failed to replace question:', errorData.error || 'Unknown error');
        throw new Error(errorData.error || 'Failed to replace question');
      }

      const data = await res.json();
      if (data.question) {
        setActiveQuestion(data.question);
      } else {
        console.error('No question returned from replace endpoint');
      }
    } catch (err) {
      console.error('Failed to replace question:', err);
      // Keep the existing question on error
    } finally {
      setQuestionLoading(false);
    }
  };

  // Set default toggle state based on goal
  useEffect(() => {
    if (activeGoal) {
      const goalsDefaultOn = ['interview'];
      setIsAnalysisModeActive(goalsDefaultOn.includes(activeGoal));
    }
  }, [activeGoal]);

  // Handle post-analysis score check
  useEffect(() => {
    if (result && activeQuestion && isAnalysisModeActive) {
      if (currentAnalysisId) {
        // First fetch the analysis to check if it's related to the question
        fetch(`${apiBase}/api/analyses/${currentAnalysisId}`, {
          headers: { 'X-Clerk-User-Id': user.id }
        })
          .then(res => res.json())
          .then(data => {
            if (data.analysis?.recording_question_id === activeQuestion.id) {
              // Fetch metrics to get question_score
              fetch(`${apiBase}/api/communication/metrics`, {
                headers: { 'X-Clerk-User-Id': user.id }
              })
                .then(metricsRes => metricsRes.json())
                .then(metricsData => {
                  if (metricsData && metricsData.length > 0) {
                    try {
                      // Find current analysis metrics
                      const current = metricsData.find(m => m.analyses?.id === currentAnalysisId) || metricsData[metricsData.length - 1];

                      if (!current) {
                        console.warn('[AnalysisPage] No metrics found for current analysis');
                        return;
                      }

                      // Use question_score if available, otherwise fall back to overall_score
                      const score = current.question_score !== null && current.question_score !== undefined
                        ? current.question_score
                        : (current.overall_score || 0);

                      setLastQuestionScore(score);

                      // Store question evaluation data for modal
                      if (current.question_evaluation && typeof current.question_evaluation === 'object') {
                        setQuestionEvaluation(current.question_evaluation);
                      }
                    } catch (err) {
                      console.error('[AnalysisPage] Error processing metrics:', err);
                    }

                    // Complete the question and get new one if question_score > 6.0
                    if (score > 6.0) {
                      // Call complete endpoint which will generate new question
                      fetch(`${apiBase}/api/recording-question/${activeQuestion.id}/complete`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-Clerk-User-Id': user.id
                        },
                        body: JSON.stringify({
                          score: score,
                          analysisId: currentAnalysisId
                        })
                      })
                        .then(res => res.json())
                        .then(completeData => {
                          // Mark current question as completed - don't auto-generate new one
                          // The new question will be generated when user visits /new-analysis page
                          if (completeData.question) {
                            // Clear active question so UI shows empty state with generate button
                            setActiveQuestion(null);
                          }
                          setShowSuccessModal(true);
                        })
                        .catch(err => {
                          console.error('Failed to complete question:', err);
                          setShowSuccessModal(true);
                        });
                    } else {
                      fetchAlternatives();
                    }
                  }
                })
                .catch(err => {
                  console.error('Failed to fetch metrics:', err);
                  // Fallback to old behavior
                  const score = data.analysis.overall_score || 0;
                  setLastQuestionScore(score);
                  if (score > 6.0) {
                    fetch(`${apiBase}/api/recording-question/${activeQuestion.id}/complete`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-Clerk-User-Id': user.id
                      },
                      body: JSON.stringify({
                        score: score,
                        analysisId: currentAnalysisId
                      })
                    })
                      .then(res => res.json())
                      .then(completeData => {
                        // Mark current question as completed - don't auto-generate new one
                        // The new question will be generated when user visits /new-analysis page
                        if (completeData.question) {
                          // Clear active question so UI shows empty state with generate button
                          setActiveQuestion(null);
                        }
                        setShowSuccessModal(true);
                      })
                      .catch(err => {
                        console.error('Failed to complete question:', err);
                        setShowSuccessModal(true);
                      });
                  } else {
                    fetchAlternatives();
                  }
                });
            }
          });
      }
    }
  }, [result, currentAnalysisId, activeQuestion, isAnalysisModeActive, apiBase, user?.id]);

  const fetchAlternatives = async () => {
    if (!activeQuestion) return;
    try {
      const params = new URLSearchParams({
        currentGoal: activeQuestion.focus,
        currentRole: activeQuestion.role,
        currentDifficulty: activeQuestion.difficulty,
        currentScore: lastQuestionScore
      });
      const res = await fetch(`${apiBase}/api/recording-question/alternatives?${params.toString()}`, {
        headers: { 'X-Clerk-User-Id': user.id }
      });
      if (res.ok) {
        const data = await res.json();
        setAlternatives(data.alternatives);
      }
    } catch (err) {
      console.error("Failed to fetch alternatives", err);
    }
  };

  // --- New Personalization Logic ---
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [personalizedChecklist, setPersonalizedChecklist] = useState([]);

  const generateChecklist = (analyses) => {
    if (!analyses || analyses.length < 1) {
      setPersonalizedChecklist([
        { id: 'default1', key: 'checklist.default1', text: 'Check your lighting' },
        { id: 'default2', key: 'checklist.default2', text: 'Ensure clear audio' },
        { id: 'default3', key: 'checklist.default3', text: 'Look at the lens' }
      ]);
      return;
    }

    const recent = analyses.slice(0, 3);
    const metrics = ['presence', 'voice_expression', 'clarity', 'authenticity', 'impact', 'confidence'];
    const averages = metrics.map(m => {
      const sum = recent.reduce((acc, a) => acc + (a.metrics?.[m] || 0), 0);
      return { metric: m, score: sum / recent.length };
    });

    const weakest = averages.sort((a, b) => a.score - b.score).slice(0, 3);

    const checklistMap = {
      presence: t('checklist.presence') || 'Stand tall and fill the frame',
      voice_expression: t('checklist.voice') || 'Vary your tone and pace',
      clarity: t('checklist.clarity') || 'Structure your opening clearly',
      authenticity: t('checklist.authenticity') || 'Be yourself, relax shoulders',
      impact: t('checklist.impact') || 'Focus on your key message',
      confidence: t('checklist.confidence') || 'Maintain steady eye contact'
    };

    setPersonalizedChecklist(weakest.map((w, i) => ({
      id: `weak-${i}`,
      text: checklistMap[w.metric] || w.metric,
      metric: w.metric
    })));
  };

  useEffect(() => {
    generateChecklist(metricsHistory);
  }, [metricsHistory, t]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchHistory = async () => {
      try {
        const res = await fetch(`${apiBase}/api/analyses`, {
          headers: { 'X-Clerk-User-Id': user.id }
        });
        if (res.ok) {
          const data = await res.json();
          const validAnalyses = (data.analyses || [])
            .filter(a => a.status === 'completed')
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

          setMetricsHistory(validAnalyses.slice(0, 10));
        }
      } catch (err) {
        console.error('Failed to fetch history:', err);
      }
    };
    fetchHistory();
  }, [user?.id, apiBase, userContext?.practiceCommitment]);

  const handleAnalyzeClick = () => {
    onAnalyze({
      recordingPrompt: selectedPrompt,
      recordingQuestionId: isAnalysisModeActive ? activeQuestion?.id : null,
      analyzeWithQuestion: isAnalysisModeActive
    });
  };

  const handleDismissCompletionNotices = () => {
    if (onDismissPracticeNotices) {
      onDismissPracticeNotices();
    }
  };

  useEffect(() => {
    if (id && user?.id && !viewingAnalysis && !loadedAnalysis) {
      fetch(
        `${apiBase}/api/analyses/${id}`,
        {
          headers: {
            'X-Clerk-User-Id': user.id,
            'Content-Type': 'application/json'
          }
        }
      )
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
          }
          return res.json();
        })
        .then(data => {
          if (data.analysis) {
            setLoadedAnalysis(data.analysis);
          }
        })
        .catch(err => {
          console.error('Failed to fetch analysis:', err);
          navigate('/dashboard');
        });
    }
  }, [id, user, viewingAnalysis, loadedAnalysis, navigate, apiBase]);

  useEffect(() => {
    const analysis = viewingAnalysis || loadedAnalysis;
    if (analysis?.s3_key && user?.id && !videoUrl) {
      fetch(
        `${apiBase}/api/analyses/${analysis.id}/video-url`,
        {
          headers: {
            'X-Clerk-User-Id': user.id,
            'Content-Type': 'application/json'
          }
        }
      )
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch video URL: ${res.status} ${res.statusText}`);
          }
          return res.json();
        })
        .then(data => {
          if (data.video_url) {
            setVideoUrl(data.video_url);
          }
        })
        .catch(err => {
          console.error('Failed to fetch video URL:', err);
        });
    }
  }, [viewingAnalysis, loadedAnalysis, user?.id, videoUrl, apiBase]);

  // Reset video URL when analysis changes
  useEffect(() => {
    if (!viewingAnalysis && !loadedAnalysis) {
      setVideoUrl(null);
    }
  }, [viewingAnalysis, loadedAnalysis]);

  // Original else if logic moved here
  useEffect(() => {
    const analysis = viewingAnalysis || loadedAnalysis;
    if (!analysis) {
      setVideoUrl(null);
    }
  }, [viewingAnalysis, loadedAnalysis, user, videoUrl]);

  // Check if video was deleted (s3_key is null)
  useEffect(() => {
    const analysis = viewingAnalysis || loadedAnalysis;
    if (analysis && !analysis.s3_key && videoUrl) {
      // Video was deleted, clear video URL
      setVideoUrl(null);
      setVideoDeleted(true);
    } else if (analysis && analysis.s3_key) {
      // Video exists, reset deleted state
      setVideoDeleted(false);
    }
  }, [viewingAnalysis, loadedAnalysis, videoUrl]);

  // Handle delete video click
  const handleDeleteVideoClick = () => {
    setShowDeleteVideoModal(true);
  };

  // Handle confirm delete video
  const handleConfirmDeleteVideo = async () => {
    if (!currentAnalysisData?.id || !user?.id) {
      return;
    }

    setIsDeletingVideo(true);

    try {
      const res = await fetch(`${apiBase}/api/analyses/${currentAnalysisData.id}/video`, {
        method: 'DELETE',
        headers: {
          'X-Clerk-User-Id': user.id,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Failed to delete video' }));
        throw new Error(errorData.error || 'Failed to delete video');
      }

      const data = await res.json();

      // Update state
      setVideoDeleted(true);
      setVideoUrl(null);
      setShowDeleteVideoModal(false);
      setIsDeletingVideo(false);

      // Show success toast
      setShowDeleteSuccessToast(true);
      setTimeout(() => {
        setShowDeleteSuccessToast(false);
      }, 3000);

      // Update loaded analysis to reflect s3_key = null
      if (loadedAnalysis) {
        setLoadedAnalysis({
          ...loadedAnalysis,
          s3_key: null
        });
      }

      // Track event
      captureEvent('video_deleted', {
        analysis_id: currentAnalysisData.id,
        journey_id: activeJourneyId
      });
    } catch (err) {
      console.error('Failed to delete video:', err);
      setIsDeletingVideo(false);

      // Show error message - using alert for now, could be improved with a toast
      const errorMessage = err.message || t('analysisPage.deleteVideoError', 'Error deleting video. Please try again later.');
      alert(errorMessage);

      // Track error event
      captureEvent('video_delete_error', {
        analysis_id: currentAnalysisData?.id,
        error: err.message,
        journey_id: activeJourneyId
      });
    }
  };

  // Handle download video
  const handleDownloadVideo = async () => {
    if (!videoUrl || !currentAnalysisData) {
      console.warn('Cannot download: missing videoUrl or currentAnalysisData', { videoUrl, currentAnalysisData });
      alert(t('analysisPage.downloadVideoError', 'Error downloading video. Please try again later.'));
      return;
    }

    if (isDownloadingVideo) {
      return; // Prevent multiple simultaneous downloads
    }

    setIsDownloadingVideo(true);

    try {
      console.log('Starting video download...', { videoUrl, analysisId: currentAnalysisData.id });
      
      // Get the original filename or generate one
      const filename = currentAnalysisData.video_filename || 
                       `video-${currentAnalysisData.id}.${currentAnalysisData.mime_type?.split('/')[1] || 'mp4'}`;

      // Try method 1: Fetch as blob (works for same-origin or CORS-enabled URLs)
      try {
        const response = await fetch(videoUrl, {
          method: 'GET',
          headers: {
            'Accept': 'video/*'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        
        if (!blob || blob.size === 0) {
          throw new Error('Downloaded file is empty');
        }

        console.log('Video blob received', { size: blob.size, type: blob.type });
        
        // Create a temporary URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger download
        link.click();
        
        // Small delay before cleanup to ensure download starts
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        console.log('Video download triggered successfully', { filename });

        // Track event
        captureEvent('video_downloaded', {
          analysis_id: currentAnalysisData.id,
          journey_id: activeJourneyId
        });
        
        setIsDownloadingVideo(false);
        return;
      } catch (fetchError) {
        console.warn('Fetch method failed, trying direct download:', fetchError);
        
        // Method 2: Direct download link (fallback for CORS issues)
        // This works by creating a link that points directly to the presigned URL
        const link = document.createElement('a');
        link.href = videoUrl;
        link.download = filename;
        link.target = '_blank'; // Open in new tab as fallback
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger download
        link.click();
        
        // Cleanup after a delay
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        console.log('Direct download link triggered', { filename, videoUrl });

        // Track event
        captureEvent('video_downloaded', {
          analysis_id: currentAnalysisData.id,
          journey_id: activeJourneyId,
          method: 'direct_link'
        });
        
        setIsDownloadingVideo(false);
        return;
      }
    } catch (err) {
      console.error('Failed to download video:', err);
      const errorMessage = err.message || t('analysisPage.downloadVideoError', 'Error downloading video. Please try again later.');
      alert(errorMessage);

      // Track error event
      captureEvent('video_download_error', {
        analysis_id: currentAnalysisData?.id,
        error: err.message,
        journey_id: activeJourneyId
      });
      
      setIsDownloadingVideo(false);
    }
  };

  return (
    <div className="analysisPage">
      {/* Delete Video Modal */}
      <DeleteVideoModal
        isOpen={showDeleteVideoModal}
        onClose={() => !isDeletingVideo && setShowDeleteVideoModal(false)}
        onConfirm={handleConfirmDeleteVideo}
        isDeleting={isDeletingVideo}
        isRtl={i18n.language && i18n.language.startsWith('he')}
      />

      {/* Success Toast Notification */}
      {showDeleteSuccessToast && (
        <div className="uploadSuccessToast">
          <div className="uploadSuccessToast__content">
            <CheckCircle2 className="uploadSuccessToast__icon" size={20} />
            <div className="uploadSuccessToast__text">
              <div className="uploadSuccessToast__title">
                {t('analysisPage.deleteVideoSuccess', 'Video deleted successfully. Analysis remains available.')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <div className="analysisPage__topNav">
        <div className="analysisPage__topNavContent">
          <a className="analysisPage__logo" href="/" onClick={(e) => {
            e.preventDefault();
            onBackToDashboard();
          }}>
            <Logo size={28} className="analysisPage__logoIcon" />
            <span className="analysisPage__logoText">BodAI</span>
          </a>
          <div className="analysisPage__topNavRight">
            <button
              className="analysisPage__navButton"
              onClick={onBackToDashboard}
            >
              <ArrowLeft size={18} />
              <span>{t('analysisPage.dashboard')}</span>
            </button>
            <div className="analysisPage__userButton">
              <UserButton afterSignOutUrl="/" />
            </div>
          </div>
        </div>
      </div>

      {(!isLoading && !result && !currentAnalysisData) && (
        <div className="analysisPage__header">
          <div className="analysisPage__headerContent">
            <div className="analysisPage__headerText">
              <h1 className="analysisPage__title">{t('analysisPage.title')}</h1>
              <p className="analysisPage__subtitle">{t('analysisPage.subtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {currentAnalysisData && (
        <div className="analysisPage__header">
          <div className="analysisPage__headerContent analysisPage__headerContent--view">
            <div className="analysisPage__headerText">
              <span className="analysisPage__crumb">{t('analysisPage.analysisOverview')}</span>
              <h1 className="analysisPage__title analysisPage__title--compact">
                {currentAnalysisData?.video_filename}
              </h1>
              <p className="analysisPage__subtitle analysisPage__subtitle--muted">
                {new Date(currentAnalysisData.created_at).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="analysisPage__content">
        {currentAnalysisData ? (
          <div className="analysisPage__viewLayout">
            <div className="analysisPage__viewColumn analysisPage__viewColumn--result">
              <div className="analysisPage__result analysisPage__result--compact">
                <AnalysisResult
                  markdown={currentAnalysisData.analysis_result}
                  loading={false}
                  analysisId={currentAnalysisData.id}
                  viewingAnalysis={currentAnalysisData}
                />
              </div>
            </div>

            <div className="analysisPage__viewColumn">
              <div className="analysisPage__analysisMetaCard">
                {videoDeleted && (
                  <div className="analysisPage__deletedNotice">
                    <div className="analysisPage__deletedNoticeIcon">🗑️</div>
                    <div className="analysisPage__deletedNoticeText">
                      <strong>{t('analysisPage.videoDeletedByUser', 'Video deleted by user')}</strong>
                      <span>{t('analysisPage.videoDeletedByUserNote', 'This video was deleted by you. The analysis remains available for viewing.')}</span>
                    </div>
                  </div>
                )}
                <div className="analysisPage__analysisMetaRow">
                  <span className="analysisPage__metaLabel">{t('analysisPage.uploadedOn')}</span>
                  <span className="analysisPage__metaValue">
                    {new Date(currentAnalysisData.created_at).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="analysisPage__analysisMetaRow">
                  <span className="analysisPage__metaLabel">{t('analysisPage.fileName')}</span>
                  <span className="analysisPage__metaValue">{currentAnalysisData.video_filename}</span>
                </div>
                {currentPromptDefinition && (
                  <div className="analysisPage__analysisMetaRow analysisPage__analysisMetaRow--stacked">
                    <span className="analysisPage__metaLabel">{t('analysisPage.sessionPrompt')}</span>
                    <div className="analysisPage__promptMeta">
                      <strong>{currentPromptDefinition.title}</strong>
                      {currentPromptDefinition.description && (
                        <p>{currentPromptDefinition.description}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="analysisPage__videoCard">
                <div className="analysisPage__videoCardHeader">
                  <h4 className="analysisPage__videoTitle">{t('analysisPage.videoPreview')}</h4>
                  {videoUrl && !videoDeleted && currentAnalysisData?.s3_key && (
                    <div className="analysisPage__videoCardActions">
                      <button
                        className="analysisPage__downloadVideoButton"
                        onClick={handleDownloadVideo}
                        disabled={isDownloadingVideo}
                        aria-label={t('analysisPage.downloadVideo', 'Download Video')}
                        title={t('analysisPage.downloadVideo', 'Download Video')}
                      >
                        {isDownloadingVideo ? (
                          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                        ) : (
                          <Download size={16} />
                        )}
                      </button>
                      <button
                        className="analysisPage__deleteVideoButton"
                        onClick={handleDeleteVideoClick}
                        aria-label={t('analysisPage.deleteVideo', 'Delete Video')}
                        title={t('analysisPage.deleteVideo', 'Delete Video')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
                {videoUrl && !videoDeleted ? (
                  <div className="analysisPage__videoFrame">
                    <VideoPlayer
                      videoUrl={videoUrl}
                      mimeType={currentAnalysisData.mime_type}
                    />
                  </div>
                ) : (
                  <div className={`analysisPage__videoPlaceholder ${videoDeleted ? 'analysisPage__videoPlaceholder--deleted' : ''}`}>
                    <div className="analysisPage__videoPlaceholderIcon">
                      {videoDeleted ? '🗑️' : '🎥'}
                    </div>
                    {videoDeleted && (
                      <div className="analysisPage__videoDeletedBadge">
                        {t('analysisPage.videoDeletedByUser', 'Video deleted by user')}
                      </div>
                    )}
                    <p>
                      {videoDeleted
                        ? t('analysisPage.videoDeletedByUserNote', 'This video was deleted by you. The analysis remains available for viewing.')
                        : t('analysisPage.videoNotAvailable')
                      }
                    </p>
                  </div>
                )}
              </div>

              <div className="analysisPage__quickActions">
                <button
                  className="btn btn--primary btn--fullWidth"
                  onClick={() => {
                    navigate('/new-analysis');
                    onRemove?.();
                    setVideoUrl(null);
                  }}
                >
                  <Plus size={18} />
                  <span>{t('analysisPage.newAnalysis')}</span>
                </button>
                <button
                  className="btn btn--secondary btn--fullWidth"
                  onClick={() => navigate('/practice')}
                >
                  <Target size={18} />
                  <span>{t('analysisPage.goToPractice')}</span>
                </button>
                <button
                  className="btn btn--secondary btn--fullWidth"
                  onClick={onBackToDashboard}
                >
                  <LayoutDashboard size={18} />
                  <span>{t('analysisPage.dashboard')}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* New analysis flow */}
            {showNewAnalysisFlow && (
              <div className="analysisPage__newLayout">
                <div className="analysisPage__mainColumn">
                  <div className="analysisPage__dashboardContainer">
                    {practiceCompletionNotices && practiceCompletionNotices.length > 0 && (
                      <div className="analysisPage__completionNotice">
                        <div>
                          {practiceCompletionNotices.map((notice) => (
                            <div key={notice.actionItemId || notice.title}>
                              <strong>{notice.title || t('analysisPage.optionalExercise')}</strong>
                              {notice.score !== undefined && (
                                <span> • {t('analysisPage.score', { score: notice.score.toFixed(1) })}</span>
                              )}
                              <p>{t('analysisPage.greatWork')}</p>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="analysisPage__completionDismiss"
                          onClick={handleDismissCompletionNotices}
                        >
                          {t('common.buttons.dismiss')}
                        </button>
                      </div>
                    )}
                    <div className="analysisPage__focusSection">
                      {!hasCompletedAnalysis && !selectedPrompt && (
                        <div className="analysisPage__emptyState">
                          <p>{t('analysisPage.completeFirstAnalysis')}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedPrompt && (
                    <div className="analysisPage__promptCard">
                      <div className="analysisPage__cardHeader">
                        <div className="analysisPage__cardHeaderIcon analysisPage__cardHeaderIcon--prompt">
                          <Video size={18} />
                        </div>
                        <div>
                          <p className="analysisPage__cardTitle">{selectedPrompt?.source === 'state' ? t('onboarding.starterScenarios.title') : t('analysisPage.optionalExercise')}</p>
                          <span className="analysisPage__cardSubtitle">
                            {selectedPrompt ? t('analysisPage.focus', { title: selectedPrompt.title }) : t('analysisPage.tryQuickDrill')}
                          </span>
                        </div>
                      </div>

                      <div className="analysisPage__promptBody">
                        <div className="analysisPage__promptHeaderRow">
                          <h4>{selectedPrompt.title}</h4>
                          <button
                            type="button"
                            className="analysisPage__promptClear"
                            onClick={() => {
                              setSelectedPrompt(null);
                              setPromptCleared(true);
                            }}
                          >
                            {t('analysisPage.clear')}
                          </button>
                        </div>
                        {selectedPrompt.description && <p className="analysisPage__promptDescription">{selectedPrompt.description}</p>}

                        {selectedPrompt.source === 'state' && selectedPrompt.id?.startsWith('baseline_') && (
                          <div className="analysisPage__starterScript">
                            <div className="starterScript__header">
                              <Lightbulb size={14} />
                              <span>{t('dashboard.sampleScriptTitle')}</span>
                            </div>
                            <p className="starterScript__content">
                              {t(`onboarding.starterScenarios.${selectedPrompt.id.replace('baseline_', '')}.script`)}
                            </p>
                          </div>
                        )}

                        <div className="analysisPage__promptMetaStack">
                          {renderPromptMeta('Setup', selectedPrompt.setup)}
                          {renderPromptMeta('What to notice', selectedPrompt.whatToNotice)}
                          {renderPromptMeta('Recording tip', selectedPrompt.recordingTip)}
                        </div>
                        {(selectedPrompt.targetMetric || selectedPrompt.difficulty || selectedPrompt.estimatedTime) && (
                          <div className="analysisPage__promptChips">
                            {selectedPrompt.targetMetric && (
                              <span className="analysisPage__promptChip">
                                {formatMetricLabel(selectedPrompt.targetMetric, t)}
                              </span>
                            )}
                            {selectedPrompt.difficulty && (
                              <span className="analysisPage__promptChip">
                                {selectedPrompt.difficulty.charAt(0).toUpperCase() + selectedPrompt.difficulty.slice(1)}
                              </span>
                            )}
                            {selectedPrompt.estimatedTime && (
                              <span className="analysisPage__promptChip">
                                {selectedPrompt.estimatedTime}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && !result && (
                    <RecordingQuestionCard
                      question={activeQuestion}
                      onRefresh={handleRefreshQuestion}
                      onToggleAnalysisMode={setIsAnalysisModeActive}
                      isAnalysisModeActive={isAnalysisModeActive}
                      loading={questionLoading}
                      onGenerate={!activeQuestion ? () => generateNewQuestion() : undefined}
                      isEmpty={!activeQuestion && !questionLoading}
                    />
                  )}

                  {!isLoading && !result && (
                    <div className="analysisPage__upload analysisPage__uploadCard">
                      <UploadVideo file={file} onSelect={onSelect} onClear={onRemove} />
                      <div className="analysisPage__uploadNote">
                        <Timer size={14} />
                        <span>{t('analysisPage.minVideoDuration', { defaultValue: 'Minimum 30 seconds required for analysis' })}</span>
                      </div>
                    </div>
                  )}

                  {file && !isLoading && (
                    <div className="analysisPage__actions analysisPage__actions--left">
                      <button
                        className="analysisPage__analyzeButton"
                        onClick={handleAnalyzeClick}
                        disabled={!file || isLoading}
                      >
                        <span>{t('analysisPage.analyzeVideo')}</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="analysisPage__sideColumn">
                  <div className="analysisPage__tipsCard">
                    <div className="analysisPage__cardHeader">
                      <div className="analysisPage__cardHeaderIcon analysisPage__cardHeaderIcon--gold">
                        <Lightbulb size={18} />
                      </div>
                      <div>
                        <p className="analysisPage__cardTitle">{t('analysisPage.preFlightCheck') || 'Pre-Flight Check'}</p>
                      </div>
                    </div>
                    <div className="analysisPage__tipsList">
                      {personalizedChecklist.map((item) => (
                        <div key={item.id} className="analysisPage__tipItem">
                          <div className="analysisPage__tipIcon">
                            <CheckCircle2 size={18} />
                          </div>
                          <div className="analysisPage__tipContent">
                            <p className="analysisPage__tipDescription">
                              {item.key ? t(item.key) : item.text}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {file ? (
                    <div className="analysisPage__preview analysisPage__preview--floating">
                      <VideoPlayer file={file} />
                    </div>
                  ) : (
                    <div className="analysisPage__previewPlaceholder">
                      <div className="analysisPage__previewText">
                        <p>{t('analysisPage.readyWhenYouAre')}</p>
                        <span>{t('analysisPage.uploadPreview')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isLoading && (
              <div className="analysisPage__loading">
                <LoadingView active fileSize={file?.size} phase={loadingPhase} uploadProgress={uploadProgress} />
              </div>
            )}

            {errorInfo && !isLoading && (
              <div className="analysisPage__error">
                <ErrorDisplay
                  error={errorInfo.error}
                  errorCategory={errorInfo.errorCategory}
                  correlationId={errorInfo.correlationId}
                  retryable={errorInfo.retryable}
                  onRetry={onRetryAnalysis}
                  fileName={errorInfo.fileName}
                  uploadedOn={errorInfo.uploadedOn}
                />
              </div>
            )}

            {result && !isLoading && !errorInfo && (
              <div className="analysisPage__viewLayout">
                <div className="analysisPage__viewColumn analysisPage__viewColumn--result">
                  {activeQuestion && lastQuestionScore !== null && lastQuestionScore <= 6.0 && (
                    <QuestionRetryCard
                      score={lastQuestionScore}
                      onRetrySame={() => {
                        onRemove?.();
                        setVideoUrl(null);
                        navigate('/new-analysis');
                      }}
                      onNewQuestion={() => {
                        handleRefreshQuestion().then(() => {
                          navigate('/new-analysis');
                        });
                      }}
                      onSelectAlternative={(alt) => {
                        generateNewQuestion({
                          role: alt.suggestedRole,
                          difficulty: alt.suggestedDifficulty
                        }).then(() => {
                          navigate('/new-analysis');
                        });
                      }}
                      alternatives={alternatives}
                    />
                  )}
                  <div className="analysisPage__result">
                    <AnalysisResult
                      markdown={result}
                      loading={false}
                      analysisId={currentAnalysisId}
                      activeQuestion={activeQuestion}
                    />
                  </div>
                </div>
                <div className="analysisPage__viewColumn">
                  <div className="analysisPage__analysisMetaCard">
                    <div className="analysisPage__analysisMetaRow">
                      <span className="analysisPage__metaLabel">{t('analysisPage.uploadedOn')}</span>
                      <span className="analysisPage__metaValue">
                        {new Date().toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    {file?.name && (
                      <div className="analysisPage__analysisMetaRow">
                        <span className="analysisPage__metaLabel">{t('analysisPage.fileName')}</span>
                        <span className="analysisPage__metaValue">{file.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="analysisPage__videoCard">
                    <h4 className="analysisPage__videoTitle">{t('analysisPage.videoPreview')}</h4>
                    {file ? (
                      <div className="analysisPage__videoFrame">
                        <VideoPlayer file={file} />
                      </div>
                    ) : (
                      <div className="analysisPage__videoPlaceholder">
                        <div className="analysisPage__videoPlaceholderIcon">🎥</div>
                        <p>{t('analysisPage.videoNotAvailableSession')}</p>
                      </div>
                    )}
                  </div>

                  <div className="analysisPage__quickActions">
                    <button
                      className="btn btn--primary btn--fullWidth"
                      onClick={() => {
                        navigate('/new-analysis');
                        onRemove?.();
                        setVideoUrl(null);
                      }}
                    >
                      <Plus size={18} />
                      <span>{t('analysisPage.newAnalysis')}</span>
                    </button>
                    <button
                      className="btn btn--secondary btn--fullWidth"
                      onClick={() => navigate('/practice')}
                    >
                      <Target size={18} />
                      <span>{t('analysisPage.goToPractice')}</span>
                    </button>
                    <button
                      className="btn btn--secondary btn--fullWidth"
                      onClick={onBackToDashboard}
                    >
                      <LayoutDashboard size={18} />
                      <span>{t('analysisPage.dashboard')}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <QuestionSuccessModal
        open={showSuccessModal}
        score={lastQuestionScore}
        questionScore={lastQuestionScore}
        questionEvaluation={questionEvaluation}
        onClose={() => {
          setShowSuccessModal(false);
          setQuestionEvaluation(null);
        }}
        onNext={() => {
          setShowSuccessModal(false);
          setQuestionEvaluation(null);
          navigate('/new-analysis');
          window.location.reload();
        }}
      />
    </div>
  );
}
